import { faCloudDownload, faExternalLink, faFiles, faListTimeline, faPrint } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/common';
import { FeatureFlag, useFeatureFlag } from 'src/helpers/featureFlag';
import { useDownloadTenderZipFile } from 'src/models/procurements/Tender/hooks';
import { IProcurementFilesCategory } from 'src/models/procurements/Tender/types';
import { trackPrintSummary } from 'src/segment/events';
import { TextSelectOptions } from 'src/shared';
import styles from './index.module.scss';
import FilesContent from 'src/pages/TenderPage/ProcurementFiles/FilesContent';

interface IProps {
  procurementFiles: IProcurementFilesCategory;
  procurementName: string;
  procurementId: string;
  className?: string;
  linkToDocsSource?: string;
  bidId?: string;
  onSelectTextInDoc?: (data: TextSelectOptions, filePath: string) => JSX.Element;
}

export const ProcurementFiles: FC<IProps> = props => {
  const { procurementFiles, procurementId, procurementName, className, linkToDocsSource, bidId } = props;
  const { filesAmount } = procurementFiles;
  const { t } = useTranslation();

  const { getFile: getZipFile, loading: zipFileLoading } = useDownloadTenderZipFile();

  const isPdfDownloadFeature = useFeatureFlag(FeatureFlag.PDF_Download);

  const onPrint = useCallback(() => {
    trackPrintSummary({ id: procurementId, name: procurementName });
    window.print();
  }, [procurementId, procurementName]);

  const onGetZIPClick = useCallback(() => {
    getZipFile(procurementName);
  }, [getZipFile, procurementName]);

  return (
    <div className={classNames(styles.files, className)}>
      {!!isPdfDownloadFeature && (
        <section className={classNames(styles.section, styles.printSection)}>
          <h3 className={styles.sectionTitle}>
            <FontAwesomeIcon icon={faListTimeline} />
            {t('DetailsPage.summaryTender')}
          </h3>
          <div className={styles.summaryContent}>
            <Button type={'primary'} onClick={onPrint}>
              <FontAwesomeIcon icon={faPrint} /> {t('DetailsPage.printPdf')}
            </Button>
          </div>
        </section>
      )}
      <section className={classNames(styles.section, styles.filesSection)}>
        <h3 className={styles.sectionTitle}>
          <FontAwesomeIcon icon={faFiles} />
          {t('KeywordSearch.documents')}
        </h3>
        <div className={styles.filesContent}>
          {!!filesAmount ? (
            <>
              <FilesContent
                procurementFiles={procurementFiles}
                procurementId={procurementId}
                eventSource={bidId ? 'Documents - Customer report page' : 'Documents - Details page'}
              />
              <span className={styles.warningText}>{t('DetailsPage.docsWarning')}</span>
            </>
          ) : (
            <span className={styles.emptyText}>{t('DetailsPage.noDocsAvailable')}</span>
          )}
          {linkToDocsSource && (
            <div className={styles.filesLink}>
              <a className={styles.tenderLink} rel="noopener noreferrer" target="_blank" href={linkToDocsSource}>
                <FontAwesomeIcon icon={faExternalLink} className={styles.tenderLinkIcon} />
                <span>{t('DetailsPage.linkToDocsSource')}</span>
              </a>
            </div>
          )}
          {!!filesAmount && (
            <div className={styles.downloadContainer}>
              <Button
                type={'primary'}
                onClick={onGetZIPClick}
                loading={zipFileLoading}
                className={styles.downloadButton}
                icon={<FontAwesomeIcon icon={faCloudDownload} />}
              >
                {t('DetailsPage.downloadAllDocuments')}
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ProcurementFiles;
